import React, { PropsWithChildren, ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonTypes,
  MenuItemModalProps,
  Modifier,
  ModifierGroupInterface,
  ProductInterface,
} from '../../services/exports/Interfaces';
import CustomButton from '../../components-library/buttons/CustomButton';
import CounterButton from '../../components-library/buttons/CounterButton';
import CustomNavBar from '../../components-library/navigation/CustomNavBar';
import useAlert from '../../hooks/utility/useAlert';
import useScreenType from '../../hooks/utility/useScreenType';
import { MODAL_SIZES } from '../../services/exports/Constants';
import CustomModal from '../../components-library/CustomModal';
import useOrderingDisabledStatus from '../../hooks/availability/useOrderingDisabledStatus';
import PriceComparison from '../../components-library/menu/PriceComparison';
import Theme from '../../components-library/Theme';
import ProductIdentifiers from '../../components-library/menu/product/ProductIdentifiers';
import useTheme from '../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import ButtonFooter from '../../components-library/footers/ButtonFooter';
import useAppState from '../../hooks/global/useAppState';
import useMenu from '../../hooks/menu/useMenu';
import useBasket from '../../hooks/basket/useBasket';
import TextTransformationResource from '../../services/resources/TextTransformationResource';
import collect, { Collection } from 'collect.js';
import useAppMethods from '../../hooks/utility/useAppMethods';
import TextArea from '../../components-library/inputs/TextArea';
import SeoResource from '../../services/resources/SeoResource';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import EarnCashbackTag from '../../components-library/cashback/EarnCashbackTag';
import EarnPointsTag from '../../components-library/loyalty-program/EarnPointsTag';
import ModifierGroup from '../../components-library/menu/ModifierGroup';
import useRewardAccount from '../../hooks/loyalty/useRewardAccount';
import MenuItemModal from './MenuItemModal';
import useMenuItemModifiers from './useMenuItemModifiers';
import BasketHelpers from '../../services/helpers/BasketHelpers';
import { BasketLineItem, BasketLineItemModifier } from '../../store/types';
import useAllergens from '../../hooks/menu/useAllergens';
import classnames from 'classnames';
import HeartIcon from '../../assets/logo/HeartIcon';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useProductLikes from '../../hooks/global/useProductLikes';
import { isNumber } from 'lodash';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';
import toast from 'react-hot-toast';
import FeedbackLabel from '../../components-library/labels/FeedbackLabel';
import WarningIcon from '../../assets/logo/WarningIcon';
import useFreeItemOffer from '../../features/offers/hooks/useFreeItemOffer';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { Modals } from '../../modules/mobile/exports/Enums';
import useBogoItemOffer from '../../features/offers/hooks/useBogoItemOffer';
import TrustLabel from '../../components-library/labels/TrustLabel';

const MenuItemsModal = (props: Partial<MenuItemModalProps>) => {
  const { hasHeader = true } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:MenuItemsModal' });

  const { menu, menuItemModalProps } = useAppState();
  const { screenPosition } = menu;
  const {
    isFreeItem,
    product: productProp,
    onCloseModal,
    lineItem: lineItemProp,
    basketItemIndex,
    showProductAddedToast,
    onBack,
  } = collect(props).keys().isNotEmpty() ? props : menuItemModalProps;

  const alert = useAlert();
  const { isDesktop, isMobile } = useScreenType();
  const theme = useTheme();
  const { findFreeProduct, compileProduct, getUpsellModifierGroup } = useMenu();
  const { compileItem, addItem, items, updateItem } = useBasket();
  const { render: renderOrderingDisabled } = useOrderingDisabledStatus();
  const { toggleMenuItemModal, toggleOrderBasketModal, toggleFreeItemsModal } = useAppMethods();
  const { trackViewItem } = useAnalytics();
  const { hasFreeItem } = useRewardAccount();
  const { isMobileApp } = useMobileApp();
  const { openModal } = useModalRouter();
  const product = isFreeItem && hasFreeItem ? findFreeProduct(productProp?.id) : compileProduct(productProp?.id);
  const freeItemOffer = useFreeItemOffer();
  const bogoItemOffer = useBogoItemOffer();
  const isBogoItem = bogoItemOffer?.isEligible && bogoItemOffer.isEligible(product);
  const hasFreeItemOffer = freeItemOffer?.isEligible && freeItemOffer.isEligible(product);
  const upsellModifierGroup = getUpsellModifierGroup(items, product);
  const hasUpsellModifierGroup = !!upsellModifierGroup?.modifiers?.length;

  const [lineItem, setLineItem] = useState<BasketLineItem>(
    lineItemProp ?? {
      product_id: product?.id,
      quantity: isBogoItem ? 2 : 1,
      modifiers: [],
      is_free: isFreeItem && hasFreeItem,
    },
  );
  const { subItem, openSubItem, closeSubItem, getSubLineItem } = useMenuItemModifiers({ scrollToModifierGroup });
  const { isAllergensOpened, setIsAllergensOpened, toggleAllergensOpened, AllergensToggleButton, NutritionInfo } =
    useAllergens();

  const updateLineItem = (field, value) =>
    setLineItem((current) => ({
      ...current,
      [field]: value,
    }));
  const offers = [bogoItemOffer?.offer, freeItemOffer?.offer, props?.offer].filter(Boolean);
  const price = useMemo<number>(() => compileItem(lineItem, 0, offers).actual_price, [lineItem]);

  const originalPrice = useMemo<number>(() => {
    const item = compileItem(lineItem, 0);
    const basePrice = item.entry.base_price;
    const modifierPrice = collect(item.modifiers).reduce((carry, modifier) => carry + modifier.entry.base_price, 0);
    const unitPrice = basePrice + modifierPrice;
    return unitPrice * lineItem.quantity;
  }, [lineItem]);

  const [scrollY, setScrollY] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(!!product?.image_url);
  const [loadingNavigation, setLoadingNavigation] = useState<boolean>(false);

  const headerRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, [product?.image_url]);

  const scrollYRef = useRef<number>(scrollY);
  scrollYRef.current = scrollY;

  const [modifierGroupRefs] = useState<Collection<React.RefObject<HTMLDivElement>>>(() =>
    collect(product?.modifier_groups ?? [])
      .when(hasUpsellModifierGroup, (collection) => collection.push(upsellModifierGroup))
      .keyBy('id')
      .map(() => React.createRef()),
  );

  function scrollToModifierGroup(id: number) {
    const ref = modifierGroupRefs.get(id);

    ref?.current && ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  const toggleModifier = (entry: ProductInterface | Modifier, modifierGroup: ModifierGroupInterface): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem: lineItem,
      modifierGroup,
      entry,
    });

    _lineItem && setLineItem(_lineItem);
  };

  function validateInput() {
    const valid = BasketHelpers.validateModifiers(product, lineItem.modifiers);

    if (!valid) {
      const invalidModifierGroup = BasketHelpers.findInvalidModifierGroup(product, lineItem.modifiers);

      invalidModifierGroup && scrollToModifierGroup(invalidModifierGroup.id);

      return alert.error({
        description: t('error_messages.missing_fields'),
      });
    }

    if (hasUpsellModifierGroup) {
      const upsellItems = lineItem.modifiers?.filter(
        (modifier) => modifier.modifier_group_id === upsellModifierGroup.id,
      );

      upsellItems.forEach((item) =>
        addItem({
          product_id: item.modifier_id,
          quantity: item.quantity,
          modifiers: item.modifiers,
        }),
      );
    }

    const nonUpsellModifiers = upsellModifierGroup?.id
      ? lineItem.modifiers?.filter((modifier) => modifier.modifier_group_id !== upsellModifierGroup.id)
      : lineItem.modifiers;

    if (isNumber(basketItemIndex)) {
      updateItem(basketItemIndex, { ...lineItem, modifiers: nonUpsellModifiers });
      goBack();
      return;
    }

    new Promise((resolve) => {
      setLoadingNavigation(true);
      addItem({ ...lineItem, modifiers: nonUpsellModifiers });
      resolve(true);
    }).then(() => {
      setTimeout(() => {
        setLoadingNavigation(false);
        showProductAddedToast && toast.success(t('success_messages.item_added'));
        if (isDesktop) toggleOrderBasketModal({ openModal: true });
        goBack();

        hasFreeItemOffer && openFreeItemsModal();
      }, 500);
    });
  }

  function openFreeItemsModal() {
    if (!isMobileApp) {
      toggleFreeItemsModal({ openModal: true, offer: freeItemOffer.offer });
      return;
    }

    openModal({
      id: Modals.FreeItemsModal,
      props: {
        offer: freeItemOffer.offer,
      },
    });
  }
  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 0);
  }, []);

  useEffect(() => {
    trackViewItem(product);
  }, []);

  function goBack(onBackAction = false): void {
    closeModal();
    setIsAllergensOpened(false);
    onCloseModal && onCloseModal();

    onBackAction && onBack && onBack();
  }

  function closeModal(): void {
    toggleMenuItemModal({
      openModal: false,
      product: null,
    });
  }

  function handleScroll(y: number): void {
    setScrollY(y);
  }

  const renderNote = useMemo<ReactElement>(() => {
    function onFocus(): void {
      if (isDesktop) {
        setTimeout(() =>
          noteRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          }),
        );
      }
    }

    return (
      <div>
        <div className="bg-gray-500 dark:bg-brand-card px-md py-xs">
          <h6>{t('note.title')}</h6>
          <p className="text-brand-text-secondary text-xs mt-[2px]">{t('note.description')}</p>
        </div>
        <div className="mt-sm px-md" ref={noteRef}>
          <TextArea
            value={lineItem.note}
            placeholder={t('note.placeholder')}
            onChange={(value) => updateLineItem('note', new TextTransformationResource(value).reduceToText())}
            autoFocus={false}
            onFocus={onFocus}
            rows={5}
          />
        </div>
      </div>
    );
  }, [lineItem.note, theme, screenPosition, isMobile, noteRef.current]);

  const renderProductInfo = (): ReactElement => {
    const shouldPresentAlcoholWarning = Boolean(product?.nutrition_info?.alcohol_by_volume_percent);
    const shouldPresentCaffeineWarning =
      product?.nutrition_info?.caffeine_mg_per_100ml > 0 ||
      product?.nutrition_info?.high_caffeine_warning_required ||
      collect(product?.nutrition_info?.allergens_list)
        .map((item) => (item ?? '').toLowerCase())
        .intersect(['koffein', 'caffeine'])
        .isNotEmpty();

    return (
      <div className="pb-md px-md">
        <div className="flex gap-xs lg:items-center items-start mb-sm relative">
          <h4 className="break-words shrink">{product?.name}</h4>
          {shouldPresentAlcoholWarning && (
            <span className="text-white bg-black rounded-md px-2xs text-xs dark:border">18+</span>
          )}
        </div>
        <div className="space-y-xs">
          <MenuItemsModal.Likes product={product} />
          <ProductIdentifiers product={product} />
          <p className="text-sm text-brand-text-secondary break-words">{product?.description}</p>
          {shouldPresentAlcoholWarning && (
            <FeedbackLabel
              message={t('alcohol_warning')}
              icon={<WarningIcon />}
              type="Notice"
              className="dark:text-black"
            />
          )}
          {shouldPresentCaffeineWarning && (
            <FeedbackLabel
              message={t('caffeine_warning')}
              icon={<WarningIcon />}
              type="Notice"
              className="dark:text-black"
            />
          )}
        </div>
        <EarnCashbackTag amount={price} className="mt-sm" />
        <TrustLabel className="mt-sm" />
        {!!product?.nutrition_info && (
          <AllergensToggleButton className="mt-sm" isOpen={isAllergensOpened} toggleAllergens={toggleAllergensOpened} />
        )}
      </div>
    );
  };

  const renderCounterButton = (): JSX.Element => {
    return (
      <CounterButton
        classes={{
          container: 'h-[43px]',
          buttons: '!w-3',
        }}
        iconColor={theme.color.brand.text.secondary}
        count={lineItem.quantity}
        increase={() => updateLineItem('quantity', lineItem.quantity + 1)}
        decrease={() => updateLineItem('quantity', lineItem.quantity - 1)}
      />
    );
  };

  const renderAddButton = (): JSX.Element => {
    if (renderOrderingDisabled) {
      return renderOrderingDisabled;
    }

    if (!product?.is_available) {
      return (
        <div className={productUnavailableContainer}>
          <h4 className={productUnavailableTextStyle}>{t('error_messages.product_unavailable')}</h4>
        </div>
      );
    }

    if (product?.actual_price === 0) {
      return (
        <ButtonFooter className={buttonContainer}>
          <CustomButton title={t('buttons.add_to_card')} onClick={validateInput} loading={loadingNavigation} />
        </ButtonFooter>
      );
    }

    const buttonTitle = basketItemIndex ? t('buttons.update_item') : t('buttons.add_to_card');

    return (
      <ButtonFooter className={addButtonContainer}>
        <div className="lg:grid lg:grid-cols-12 flex gap-[10px] items-center">
          <div className="lg:col-span-4 shrink-none min-w-[90px]">{renderCounterButton()}</div>
          <div className="lg:col-span-8 flex-1 shrink-0">
            <CustomButton
              title={
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className="text-brand-button-primary-text line-clamp-1 text-sm">{buttonTitle}</span>
                  <PriceComparison
                    actualPrice={price}
                    originalPrice={originalPrice}
                    originalPriceStyle={{
                      fontSize: Theme.font.size.sm,
                      color: theme.buttons[ButtonTypes.primary].text,
                    }}
                    actualPriceStyle={{
                      fontSize: Theme.font.size.sm,
                      color: theme.buttons[ButtonTypes.primary].text,
                    }}
                  />
                </div>
              }
              onClick={validateInput}
              loading={loadingNavigation}
            />
          </div>
        </div>
      </ButtonFooter>
    );
  };

  const renderHeaderBar = useMemo(() => {
    if (!hasHeader) return null;

    const shouldFadeInHeader = isMobileApp || scrollYRef.current > (headerRef.current?.clientHeight ?? 0);

    return (
      <div className={headerContainer} ref={headerRef}>
        {!!product?.image_url && <div className={headerBackground(shouldFadeInHeader)} />}
        <CustomNavBar
          rightClick={() => goBack(true)}
          hideBackIcon
          containerClassName={classnames({ 'bg-brand-background-primary py-sm': !product?.image_url })}
          transparent={!!product?.image_url}
        />
      </div>
    );
  }, [isDesktop, product?.image_url, scrollYRef.current, headerRef.current, hasHeader, isMobileApp]);

  const renderProductPicture = useMemo((): JSX.Element | null => {
    return product?.image_url ? (
      <div
        className={productImageContainer}
        style={{
          marginTop: -headerHeight,
        }}
      >
        <img
          src={product?.thumbnail_url ?? product?.image_url}
          alt={SeoResource.toAltText(`${product?.name} image`)}
          className={productImage}
        />
        <EarnPointsTag price={price} quantity={lineItem.quantity} className={classnames('absolute left-md top-md')} />
      </div>
    ) : null;
  }, [product?.image_url, scrollYRef.current, headerHeight, isMobileApp, product?.likes_count]);

  const updateSubLineItem = (entryLineItem: BasketLineItemModifier): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem,
      modifierGroup: subItem.modifierGroup,
      entry: subItem.item,
      entryLineItem,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const adjustQuantity = (
    modifierGroup: ModifierGroupInterface,
    entry: Modifier | ProductInterface,
    step: number,
  ): void => {
    const _lineItem = BasketHelpers.adjustQuantity({
      lineItem: lineItem,
      entry,
      modifierGroup,
      step,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const getSelectedModifiers = (modifierGroup: ModifierGroupInterface): BasketLineItemModifier[] =>
    lineItem.modifiers?.filter((item) => item.modifier_group_id === modifierGroup.id);

  return (
    <Wrapper fullScreen={!isDesktop} goBack={() => goBack(true)} onScroll={handleScroll} loading={loading}>
      {subItem ? (
        <MenuItemModal
          data={subItem.item}
          selectedLineItem={getSubLineItem(lineItem)}
          scrollY={scrollY}
          goBack={closeSubItem}
          updateParentLineItem={updateSubLineItem}
        />
      ) : (
        <>
          <div className={modalContainer}>
            {renderHeaderBar}
            {renderProductPicture}
            <div className={wrapper(!!product?.image_url)}>
              {renderProductInfo()}
              {isAllergensOpened ? (
                <NutritionInfo data={product} className="px-md" />
              ) : (
                <>
                  <div className="mb-md">
                    {product?.modifier_groups?.map((modifierGroup) => (
                      <ModifierGroup
                        data={modifierGroup as ModifierGroupInterface<CompiledModifier>}
                        selectedModifiers={getSelectedModifiers(modifierGroup)}
                        toggle={(entry: CompiledProduct | CompiledModifier) => toggleModifier(entry, modifierGroup)}
                        key={`modifier-group-${modifierGroup.id}`}
                        ref={modifierGroupRefs.get(modifierGroup.id)}
                        openSubItem={(item: CompiledProduct) => openSubItem(item, modifierGroup)}
                        adjustQuantity={(entry, step) => adjustQuantity(modifierGroup, entry, step)}
                      />
                    ))}
                    {renderNote}
                  </div>

                  {hasUpsellModifierGroup && (
                    <ModifierGroup
                      isUpsell
                      data={upsellModifierGroup as any as ModifierGroupInterface<CompiledModifier>}
                      selectedModifiers={getSelectedModifiers(upsellModifierGroup)}
                      toggle={(entry: CompiledProduct | CompiledModifier) => toggleModifier(entry, upsellModifierGroup)}
                      ref={modifierGroupRefs.get(upsellModifierGroup.id)}
                      openSubItem={(item: CompiledProduct) => openSubItem(item, upsellModifierGroup)}
                      adjustQuantity={(entry, step) => adjustQuantity(upsellModifierGroup, entry, step)}
                    />
                  )}
                </>
              )}
            </div>
            {!isAllergensOpened && renderAddButton()}
          </div>
        </>
      )}
    </Wrapper>
  );
};

interface WrapperProps extends PropsWithChildren {
  goBack: () => void;
  onScroll: (y: number) => void;
  loading: boolean;
  fullScreen: boolean;
}

function Wrapper(props: WrapperProps) {
  const { children, goBack, onScroll, loading, fullScreen } = props;
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return <>{children}</>;
  }

  return (
    <CustomModal
      open={true}
      size={MODAL_SIZES.SM}
      fullScreen={fullScreen}
      onClose={goBack}
      onScroll={onScroll}
      loading={loading}
      containerClassName="h-full lg:h-[95vh]"
    >
      {children}
    </CustomModal>
  );
}

export default MenuItemsModal;

const modalContainer = 'bg-brand-background-primary size-full relative flex-col flex';

const headerContainer = 'sticky top-0 z-10 relative';

const headerBackground = (fadeIn: boolean) =>
  `bg-brand-background-primary transition-opacity duration-500 ease-out border-b absolute inset-0 ${fadeIn ? 'opacity-100' : 'opacity-0'}`;

const productImageContainer = 'relative w-full';

const productImage = 'object-cover size-full lg:h-[320px] w-full';

const wrapper = (hasOffset: boolean) =>
  `pt-md lg:pb-md pb-[206px] md:pb-[148px] ${hasOffset ? 'border-t' : ''} bg-brand-background-primary relative flex-1`;

const buttonContainer = `lg:sticky fixed bottom-0`;

const addButtonContainer = `lg:sticky fixed bottom-0 py-sm space-y-8`;

const productUnavailableContainer =
  'lg:sticky fixed bottom-0 w-full flex flex-row items-center justify-center px-lg py-md bg-gray border-t';

const productUnavailableTextStyle = 'text-brand-text-secondary text-center';

MenuItemsModal.Likes = ({ product }: { product: ProductInterface }): JSX.Element => {
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = Boolean(product) && isLiked(product.id);

  return (
    Boolean(product) && (
      <div
        onClick={(e) => {
          e.stopPropagation();
          toggleProductLike(product.id);
        }}
        className="text-brand-text-secondary w-fit cursor-pointer text-sm font-medium flex items-center space-x-1"
      >
        <HeartIcon
          filled={liked}
          className={classnames('h-3', {
            'fill-danger': liked,
            'fill-gray-700': !liked,
          })}
        />
        <span className="text-xs text-brand-text-secondary">{product.likes_count}</span>
      </div>
    )
  );
};
